import { Dropdown, DropdownRef, Input, Menu, Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { AlertDiamondFilled, ChevronDown, ChevronUp } from '@recruit-robin/robin-theme/web-icons';
import { css } from '@recruit-robin/robin-theme/web-styles';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useDictionary } from '@/shared/lib/i18n';
import type { IndeedProject } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedProjectListQuery';
import { useIndeedProjectListQuery } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedProjectListQuery';

import type { IndeedBulkJobSelection } from '../model/use-indeed-bulk-job-selection';
import { IndeedCreditsSyncPill } from './indeed-credits-sync-pill/IndeedCreditsSyncPill';

const sectionCss = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const creditsRowCss = css`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const inlineErrorCss = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const dropdownCss = css`
  width: 100%;
`;

const menuCss = css`
  z-index: 3;
  width: 100%;
  max-width: 472px;
  min-width: 250px;
`;

type IndeedProjectSelectProps = {
  value: IndeedProject | null;
  onChange: (project: IndeedProject | null) => void;
};

function IndeedProjectSelect({ value, onChange }: IndeedProjectSelectProps) {
  const dictionary = useDictionary();
  const dropdownRef = useRef<DropdownRef>(null);
  const [search, setSearch] = useState('');
  const { data: projects = [] } = useIndeedProjectListQuery();

  useEffect(() => {
    if (value === null && projects.length > 0) {
      onChange(projects.find((p) => p.default) ?? projects[0]);
    }
  }, [value, projects, onChange]);

  const filtered = useMemo(
    () => projects.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [projects, search],
  );

  return (
    <Dropdown
      ref={dropdownRef}
      placement="bottom"
      flipMenu={false}
      shouldUsePortal={false}
      css={dropdownCss}
      renderMenu={
        <Menu.Root customCss={menuCss}>
          {filtered.map((project) => (
            <Menu.Item.Root
              key={project.key}
              selected={value?.key === project.key}
              onClick={() => {
                onChange(project);
                dropdownRef.current?.close();
              }}>
              {project.name}
            </Menu.Item.Root>
          ))}
        </Menu.Root>
      }>
      {({ isOpen }) => (
        <Input
          variant="gray"
          endIcon={isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder={dictionary.selectAProject}
          value={isOpen ? search : value?.name ?? ''}
          contained
        />
      )}
    </Dropdown>
  );
}

type IndeedProjectCreditsSectionProps = {
  selection: IndeedBulkJobSelection;
};

export function IndeedProjectCreditsSection({ selection }: IndeedProjectCreditsSectionProps) {
  const dictionary = useDictionary();

  return (
    <div css={sectionCss}>
      <div css={creditsRowCss}>
        <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
          {dictionary.indeedCreditsRemainingPrefix}
        </Typography>
        {selection.isIndeedCreditsOutOfSync ? (
          <IndeedCreditsSyncPill creditBalance={selection.creditBalance} />
        ) : (
          <Pill
            label={dictionary.indeedCreditsLabel(selection.creditBalance)}
            style="icon-removable"
            type="default"
            size="small"
            onClick={() => undefined}
            padding="text"
          />
        )}
        <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
          {dictionary.indeedCreditsRemainingSuffix}
        </Typography>
      </div>

      {selection.hasNoCredits && (
        <div css={inlineErrorCss}>
          <AlertDiamondFilled size={14} color={(c) => c.tertiary.red[50]} />
          <Typography variant={(v) => v.body.short} color={(c) => c.tertiary.red[50]}>
            {dictionary.indeedNoCredits}
          </Typography>
        </div>
      )}

      <IndeedProjectSelect value={selection.selectedProject} onChange={selection.setSelectedProject} />
    </div>
  );
}
